import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDictionarySearch } from '../../api/queries/storyStudio';

interface Props {
  open: boolean;
  onClose: () => void;
  bookId: string;
  initialQuery?: string;
}

/**
 * Keyboard-invoked lookup (⌘D) for names the creator types in the editor. Prefix-matches
 * entities and world elements scoped to the current book + shared library. Clicking a hit
 * navigates to the entity's detail page.
 */
export function DictionaryModal({
  open,
  onClose,
  bookId,
  initialQuery,
}: Props) {
  const [query, setQuery] = useState(initialQuery ?? '');
  const navigate = useNavigate();
  const results = useDictionarySearch(query, bookId);

  useEffect(() => {
    if (open) {
      setQuery(initialQuery ?? '');
    }
  }, [open, initialQuery]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Dictionary</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          autoFocus
          size="small"
          placeholder="Search entities and world elements…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            endAdornment: results.isFetching ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
          sx={{ mb: 2 }}
        />

        {query.length < 2 && (
          <Typography variant="caption" color="text.secondary">
            Type at least two characters to search.
          </Typography>
        )}

        {query.length >= 2 &&
          !results.isLoading &&
          (results.data ?? []).length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No matches. Entities and world elements are searched by prefix.
            </Typography>
          )}

        <List dense disablePadding>
          {(results.data ?? []).map((hit) => (
            <ListItemButton
              key={`${hit.kind}:${hit.id}`}
              onClick={() => {
                if (hit.kind === 'entity') {
                  navigate({
                    to: '/entities/$entityId',
                    params: { entityId: hit.id },
                  });
                  onClose();
                } else if (hit.kind === 'world_element') {
                  // World elements don't have dedicated detail pages today; open the
                  // world-builder of their parent world. The dictionary payload doesn't
                  // include worldId, so we fall back to the creator's worlds index.
                  navigate({ to: '/worlds' });
                  onClose();
                }
              }}
            >
              <ListItemText
                primary={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">{hit.name}</Typography>
                    <Chip size="small" label={hit.kind.replace(/_/g, ' ')} />
                  </Stack>
                }
                secondary={hit.description ?? ''}
              />
            </ListItemButton>
          ))}
        </List>
      </DialogContent>
    </Dialog>
  );
}
